import { useState } from "react";
import { supabase } from "@/lib/supabase";
import type { Reflection } from "../types";

interface UseDeleteReflectionProps {
	userId: string | undefined;
	reflections: Reflection[];
	setReflections: (reflections: Reflection[]) => void;
}

export const useDeleteReflection = ({
	userId,
	reflections,
	setReflections,
}: UseDeleteReflectionProps) => {
	const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
	const [reflectionToDelete, setReflectionToDelete] = useState<string | null>(
		null,
	);
	const [deleteLoading, setDeleteLoading] = useState(false);

	const handleDeleteClick = (id: string) => {
		setReflectionToDelete(id);
		setDeleteDialogOpen(true);
	};

	const confirmDelete = async () => {
		if (!reflectionToDelete) return;
		if (!userId) {
			alert("Unable to delete reflection: user not authenticated.");
			return;
		}

		try {
			setDeleteLoading(true);
			const { error } = await supabase
				.from("reflections")
				.delete()
				.eq("id", reflectionToDelete)
				.eq("user_id", userId);

			if (error) throw error;

			setReflections(reflections.filter((r) => r.id !== reflectionToDelete));
			setDeleteDialogOpen(false);
			setReflectionToDelete(null);
		} catch (error) {
			console.error("Error deleting reflection:", error);
			alert("Failed to delete reflection. Please try again.");
		} finally {
			setDeleteLoading(false);
		}
	};

	const cancelDelete = () => {
		setDeleteDialogOpen(false);
		setReflectionToDelete(null);
	};

	return {
		deleteDialogOpen,
		setDeleteDialogOpen,
		deleteLoading,
		handleDeleteClick,
		confirmDelete,
		cancelDelete,
	};
};
